<template>
    <div
        class="header-cell"
        :class="{
            sortable: isSortable,
            'text-right': header.align === 'right',
            'text-center': header.align === 'center',
            resizing: isResizing
        }"
        :style="cellStyle"
        @click="handleClick">
        <div class="cell-content">
            <span class="header-text">{{ header.text }}</span>
            <v-icon
                v-if="isSortable"
                class="sort-icon"
                small>
                {{ sortIcon }}
            </v-icon>
        </div>
        <div
            v-if="!isLast && header.value !== 'actions'"
            class="resize-handle"
            @mousedown.stop="$emit('resize-start', $event)"
            @click.stop />
    </div>
</template>

<script>
export default {
    props: {
        header: {
            type: Object,
            required: true
        },
        width: {
            type: Number,
            default: undefined
        },
        isLast: {
            type: Boolean,
            required: true
        },
        shouldGrow: {
            type: Boolean,
            required: true
        },
        isSortable: {
            type: Boolean,
            required: true
        },
        isSorted: {
            type: Boolean,
            required: true
        },
        sortDirection: {
            type: String,
            default: null,
            validator: value => value === null || ['asc', 'desc'].includes(value)
        },
        cellHeight: {
            type: Number,
            required: true
        },
        isResizing: {
            type: Boolean,
            default: false
        },
        recentlyResized: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        cellStyle() {
            const baseStyle = {
                height: `${this.cellHeight}px`
            };

            if (this.shouldGrow) {
                return {
                    ...baseStyle,
                    flex: '1 1 auto',
                    minWidth: '3.75rem'
                };
            }

            // Other columns have fixed widths
            const width = this.width || 60;
            
            // Actions column should have a minimum width to ensure edit button is always visible
            if (this.header.value === 'actions') {
                const finalWidth = Math.max(width, 55);
                return {
                    ...baseStyle,
                    flex: `0 0 ${finalWidth}px`,
                    width: `${finalWidth}px`,
                    minWidth: `${finalWidth}px`,
                    maxWidth: `${finalWidth}px`
                };
            }
            
            return {
                ...baseStyle,
                flex: `0 0 ${width}px`,
                width: `${width}px`
            };
        },
        sortIcon() {
            if (!this.isSorted) return 'mdi-unfold-more-horizontal';
            return this.sortDirection === 'desc' ? 'mdi-chevron-down' : 'mdi-chevron-up';
        }
    },
    methods: {
        handleClick(event) {
            // Prevent sorting if we're currently resizing or just finished resizing
            if (this.isResizing || this.recentlyResized) {
                event.preventDefault();
                event.stopPropagation();
                return;
            }

            if (this.isSortable) {
                this.$emit('sort');
            }
        }
    }
};
</script>

<style scoped>
.header-cell {
    display: flex;
    position: relative;
    align-items: center;
    border-right: 0.1rem solid var(--app-table-border, #e0e0e0);
    border-bottom: 0.1rem solid var(--app-table-border, #e0e0e0);
    padding: 0.5rem 0.5rem 0.5rem 0.5rem;
    overflow: hidden;
    font-weight: 600;
    font-size: 0.875rem;
}

.header-cell:last-child {
    border-right: none;
}

.header-cell.sortable {
    cursor: pointer;
}

.header-cell.sortable:hover:not(.resizing) {
    background-color: rgba(0, 0, 0, 0.02);
}

.header-cell.resizing {
    background-color: rgba(0, 0, 0, 0.04);
}

.cell-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    overflow: hidden;
}

.header-text {
    overflow: hidden;
    white-space: nowrap;
}

.text-right .cell-content {
    justify-content: flex-end;
}

.text-center .cell-content {
    justify-content: center;
}

.sort-icon {
    flex-grow: 0;
    flex-shrink: 0;
    width: 1rem;
    height: 1rem;
}

.resize-handle {
    position: absolute;
    top: 0;
    right: -0.5rem;
    z-index: 1;
    cursor: col-resize;
    background: transparent;
    width: 1rem;
    height: 100%;
}

.resize-handle:hover {
    background: linear-gradient(to right, transparent, rgba(0, 0, 0, 0.1), transparent);
}

.resize-handle:active {
    background: linear-gradient(to right, transparent, var(--app-primary, #1976d2), transparent);
}

</style>
